import Papa from 'papaparse';
import { format } from 'date-fns';
import { formatCurrency, getCurrencySymbol } from '@/utils/currency';
import type { Payment } from '../types/payment.types';

export interface ExportRow {
  'Influencer Name': string;
  'Campaign Name': string;
  'Payment Status': string;
  [key: string]: string | number;
}

export function getFormattedPaymentsForExport(
  payments: Payment[],
  dateRange: string,
  userCountry: string
): ExportRow[] {
  const now = new Date();
  let start: Date | null = null;
  let end: Date | null = null;

  if (dateRange === 'this-week') {
    const day = now.getDay();
    start = new Date(now);
    start.setDate(now.getDate() - ((day + 6) % 7));
    start.setHours(0, 0, 0, 0);
    end = now;
  }
  if (dateRange === 'this-month') {
    start = new Date(now.getFullYear(), now.getMonth(), 1);
    end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  }
  if (dateRange === 'last-30-days') {
    start = new Date(now);
    start.setDate(now.getDate() - 30);
    start.setHours(0, 0, 0, 0);
    end = now;
  }

  const filterByDate = (p: Payment): boolean => {
    if (!start) return true;
    if (p.status === 'paid' && p.paymentDate) {
      const pd = new Date(p.paymentDate);
      return (!end || pd <= end) && pd >= start;
    }
    if ((p.status === 'pending' || p.status === 'overdue') && p.dueDate) {
      const due = new Date(p.dueDate);
      return (!end || due <= end) && due >= start;
    }
    return true;
  };

  const filtered = payments.filter(filterByDate);
  const symbol = getCurrencySymbol(userCountry);

  return filtered.map((p) => ({
    'Influencer Name': p.influencer?.name ?? '',
    'Campaign Name': p.campaign ?? '',
    'Payment Status': (p.status ?? '')
      .replace(/-/g, ' ')
      .replace(/\b\w/g, (l) => l.toUpperCase()),
    [`Base Fee (${symbol})`]:
      typeof p.baseFee === 'number'
        ? formatCurrency(p.baseFee, userCountry)
        : (p.baseFee ?? ''),
    [`GST Amount (${symbol})`]:
      typeof p.gstAmount === 'number'
        ? formatCurrency(p.gstAmount, userCountry)
        : formatCurrency(0, userCountry),
    [`TDS Amount (${symbol})`]:
      typeof p.tdsAmount === 'number'
        ? formatCurrency(p.tdsAmount, userCountry)
        : formatCurrency(0, userCountry),
    [`Net Payable (${symbol})`]:
      typeof p.netPayable === 'number'
        ? formatCurrency(p.netPayable, userCountry)
        : (p.netPayable ?? ''),
    'Payment Date':
      p.status === 'paid' && p.paymentDate
        ? format(new Date(p.paymentDate), 'dd MMM yyyy')
        : '',
    'Due Date':
      (p.status === 'pending' || p.status === 'overdue') && p.dueDate
        ? format(new Date(p.dueDate), 'dd MMM yyyy')
        : '',
    'Mode of Payment': p.paymentMode ?? '',
    'Notes / Remarks': p.notes ?? '',
  }));
}

export function downloadPaymentsCsv(
  payments: Payment[],
  dateRange: string,
  userCountry: string,
  fileName: string
): ExportRow[] {
  const exportData = getFormattedPaymentsForExport(payments, dateRange, userCountry);

  if (exportData.length === 0) {
    return exportData;
  }

  const csv = Papa.unparse(exportData, { quotes: true });

  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 100);

  return exportData;
}
